import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';
import { cn } from '@Utils';

import { ModalComponent, ModalLoading, ModalSuccess } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFetchData } from '@CustomHooks';
import { DataSupplier, PlanDashboardItem } from '@Models';
import { bopsApi } from '@Network';

interface SupplierAddonsModalProps {
  supplier: DataSupplier;
  onClose: () => void;
  onUpdated: () => void;
}

const AddonChip = ({ label, active, onToggle }: { label: string; active: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
      active
        ? 'border-brand-200 bg-brand-50 text-brand-700'
        : 'border-gray-200 bg-white text-gray-700 hover:border-brand-300',
    )}>
    {label}
    {active ? <X size={14} /> : <Plus size={14} />}
  </button>
);

const SupplierAddonsModal = ({ supplier, onClose, onUpdated }: SupplierAddonsModalProps) => {
  const { t } = useTranslation();
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(supplier.enabled_addons?.map((a) => a.id) ?? []);

  const { data, isLoading } = useFetchData(bopsApi.getSupplierAddons, supplier.id);

  useEffect(() => {
    if (data) setSelectedIds(data.enabled.map((a) => a.id));
  }, [data]);

  const allAddons: PlanDashboardItem[] = data ? [...data.enabled, ...data.available] : [];
  const enabledItems = allAddons.filter((addon) => selectedIds.includes(addon.id));
  const availableItems = allAddons.filter((addon) => !selectedIds.includes(addon.id));

  const toggleAddon = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((addonId) => addonId !== id) : [...current, id],
    );
  };

  const handleSave = () => {
    if (!supplier.id) return;
    setOpenLoading(true);
    bopsApi
      .updateSupplierAddons(supplier.id, { enabled_addons_ids: selectedIds })
      .then(() => {
        setOpenLoading(false);
        setOpenSuccess(true);
        setTimeout(() => {
          setOpenSuccess(false);
          onUpdated();
        }, 1500);
      })
      .catch(() => {
        setOpenLoading(false);
        showErrorToast(t('Supplier.AddonsUpdateError'));
      });
  };

  return (
    <>
      <ModalComponent isOpen onClose={onClose} title={t('Supplier.AddonsModalTitle')} closeClickOutside={false}>
        <div className="w-full">
          <p className="-mt-2 mb-4 text-sm text-gray-500">{supplier.display_name}</p>

          {!isLoading && allAddons.length === 0 && (
            <p className="text-sm text-gray-500">{t('Supplier.NoAddonsAvailable')}</p>
          )}

          {enabledItems.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('Supplier.AddonsActive')}
              </div>
              <div className="flex flex-wrap gap-2">
                {enabledItems.map((addon) => (
                  <AddonChip
                    key={addon.id}
                    label={addon.dashboard_name}
                    active
                    onToggle={() => toggleAddon(addon.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {enabledItems.length > 0 && availableItems.length > 0 && <hr className="mb-4 border-gray-200" />}

          {availableItems.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('Supplier.AddonsAvailable')}
              </div>
              <div className="flex flex-wrap gap-2">
                {availableItems.map((addon) => (
                  <AddonChip
                    key={addon.id}
                    label={addon.dashboard_name}
                    active={false}
                    onToggle={() => toggleAddon(addon.id)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {t('Supplier.AddonsSelectedCount', { count: selectedIds.length })}
            </span>
            <div className="flex gap-2">
              <TrackedButton
                variant="outline"
                buttonName="SUPPLIER_ADDONS_CANCELED"
                className="bg-white"
                onClick={onClose}
                isSubmit={false}>
                {t('Global.Canceled')}
              </TrackedButton>
              <TrackedButton buttonName="SUPPLIER_ADDONS_UPDATED" isSubmit={false} onClick={handleSave}>
                <div className="text-white">{t('Supplier.Save')}</div>
              </TrackedButton>
            </div>
          </div>
        </div>
      </ModalComponent>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center -mt-4">
          <span className="text-[18px]">{t('Supplier.AddonsUpdateSuccess')}</span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center -mt-[9px]">{t('Supplier.LoadingCreate')}</div>
      </ModalLoading>
    </>
  );
};

export default SupplierAddonsModal;
